/**
 * Inventory blob loader + IndexedDB cache.
 *
 * loadInventory() resolves to { version, cars } by:
 *   1. Reading the cached blob from IndexedDB (if any) and returning
 *      immediately with that result if present.
 *   2. In parallel, hitting /api/ctgsearch/version to check freshness.
 *      If the server version differs from the cached one, fetching
 *      /api/ctgsearch.json, writing it to IndexedDB, and invoking the
 *      optional onUpdate callback with the fresh dataset so the UI can
 *      rebuild its index.
 *   3. On a cold cache (no IndexedDB entry), fetching the blob
 *      synchronously and resolving with it.
 *
 * The store always holds one row keyed "current". We don't version the
 * schema or keep history -- the server is the source of truth and a
 * cold refetch is cheap at this volume.
 */

const DB_NAME = "ctg-inventory";
const STORE = "blobs";
const KEY = "current";
const DB_VERSION = 1;

const VERSION_URL = "/api/ctgsearch/version";
const INVENTORY_URL = "/api/ctgsearch.json";


/* ------------------------------------------------------------------ */
/* IndexedDB helpers                                                  */
/* ------------------------------------------------------------------ */

function openDB() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB not available"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function readCache() {
  return openDB().then((db) => new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).get(KEY);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  })).catch(() => null); // Any IDB failure is a cold-cache condition.
}

function writeCache(blob) {
  return openDB().then((db) => new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(blob, KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  })).catch(() => {
    // IDB write failure is non-fatal; the search still works for this
    // session, we just won't have a cache next time.
    return null;
  });
}


/* ------------------------------------------------------------------ */
/* Network                                                            */
/* ------------------------------------------------------------------ */

function fetchJSON(url) {
  return fetch(url, { credentials: "same-origin" }).then((res) => {
    if (!res.ok) throw new Error("ctgsearch fetch failed: " + res.status + " " + url);
    return res.json();
  });
}


/* ------------------------------------------------------------------ */
/* Public API                                                         */
/* ------------------------------------------------------------------ */

/**
 * Load the inventory blob, preferring the IndexedDB cache but kicking
 * off a background freshness check.
 *
 * @param {Object} [opts]
 * @param {Function} [opts.onUpdate] - called with the fresh blob if the
 *   background version check turns up a newer dataset after the cached
 *   one has already been returned.
 * @returns {Promise<{version:string, cars:Array<Object>}>}
 */
export function loadInventory(opts) {
  opts = opts || {};
  const onUpdate = typeof opts.onUpdate === "function" ? opts.onUpdate : null;

  return readCache().then((cached) => {
    if (cached && Array.isArray(cached.cars) && cached.cars.length > 0) {
      // Fire a background freshness check. If versions differ, fetch
      // fresh and notify the caller via onUpdate.
      fetchJSON(VERSION_URL).then((meta) => {
        if (meta && meta.version && meta.version !== cached.version) {
          return fetchJSON(INVENTORY_URL).then((fresh) => {
            if (!fresh || !Array.isArray(fresh.cars)) return;
            writeCache({ version: fresh.version, cars: fresh.cars });
            if (onUpdate) onUpdate({ version: fresh.version, cars: fresh.cars });
          });
        }
      }).catch(() => {
        // Network failure on the background check is non-fatal; the
        // user still has their cached (possibly stale) results.
      });
      return { version: cached.version, cars: cached.cars };
    }
    // Cold cache: must fetch synchronously.
    return fetchJSON(INVENTORY_URL).then((fresh) => {
      if (!fresh || !Array.isArray(fresh.cars)) {
        throw new Error("ctgsearch inventory response has no cars[]");
      }
      writeCache({ version: fresh.version, cars: fresh.cars });
      return { version: fresh.version, cars: fresh.cars };
    });
  });
}
